import { useEffect, useRef } from 'react';
import { useNotification } from '../contexts/NotificationContext';

/**
 * 🕵️‍♂️ 아론 인사이트 (Aaron Insights - Enhanced)
 * 앱 실행 시 데이터를 분석하여 사역자에게 필요한 알림을 자동으로 제안합니다.
 * (하루 1회 실행 제한)
 * 
 * [포함된 기능]
 * 1. 생일자 알림 (KST 기준 향후 7일)
 * 2. 새가족(초신자) 케어 알림
 * 3. 장기결석자 경고 알림
 * 4. 연락처 누락 행정 알림
 */
export const useAaronInsights = (students, userProfile) => {
    const { addNotification } = useNotification();
    const isChecked = useRef(false);

    useEffect(() => {
        // 데이터가 없거나 이미 체크했으면 스킵
        if (!students || students.length === 0 || !userProfile || isChecked.current) return;

        const performAnalysis = () => {
            // 1. 하루 1회 체크 로직 (LocalStorage 사용)
            const today = new Date().toISOString().split('T')[0];
            const lastCheckKey = `aaron_check_${userProfile.uid || 'guest'}_${today}`;

            if (localStorage.getItem(lastCheckKey)) {
                // 이미 오늘 확인했음
                return;
            }

            console.log("🕵️‍♂️ 아론이 데이터를 정밀 분석 중입니다...");
            let notificationCount = 0;
            const MAX_NOTIFICATIONS = 2; // 하루 최대 알림 수 (피로도 방지)

            // 🎂 분석 1: 다가오는 생일자 (7일 이내)
            const now = new Date();
            let upcomingBirthdays = [];

            for (let i = 0; i < 7; i++) {
                const targetDate = new Date(now.getTime() + (i * 24 * 60 * 60 * 1000));
                const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
                const dd = String(targetDate.getDate()).padStart(2, '0');
                const md = `${mm}-${dd}`;

                students.forEach(s => {
                    if (s.birth) {
                        // [FIX] birth 필드가 문자열이 아닐 경우(Firebase Timestamp 등) 안전하게 변환
                        const birthStr = typeof s.birth === 'string' ? s.birth :
                            (s.birth?.toDate ? s.birth.toDate().toISOString().split('T')[0] : String(s.birth));
                        if (birthStr.includes(md)) {
                            upcomingBirthdays.push(s.name);
                        }
                    }
                });
            }

            // 생일자 알림 (최우선)
            if (upcomingBirthdays.length > 0) {
                const uniqueNames = [...new Set(upcomingBirthdays)];
                const msg = uniqueNames.length <= 2
                    ? `${uniqueNames.join(', ')}님의 생일이 다가와요! 🎂`
                    : `${uniqueNames[0]}님 외 ${uniqueNames.length - 1}명의 생일이 다가와요! 🎂`;

                addNotification(
                    "축하해주세요! 🎉",
                    msg + " 센스 있는 축하 톡으로 감동을 선물해보세요.",
                    "info"
                );
                notificationCount++;
            }

            // 🌱 분석 2: 새가족 케어 (초신자)
            const newComers = students.filter(s => s.faithState === '초신자');
            if (newComers.length > 0 && notificationCount < MAX_NOTIFICATIONS) {
                // 랜덤하게 한 명의 이름을 예시로 듦
                const targetStudent = newComers[Math.floor(Math.random() * newComers.length)];
                const target = targetStudent.name;
                addNotification(
                    "따뜻한 환영 🌱",
                    `${target}님이 아직 낯설어 할 수 있어요. 따뜻한 환영 문자로 마음의 거리를 좁혀볼까요?`,
                    "success",
                    "pastoral",
                    targetStudent.id,
                    null,
                    "students"
                );
                notificationCount++;
            }

            // 📉 분석 3: 장기결석자 (위험군)
            const absentees = students.filter(s => s.faithState === '장기결석' || s.faithState === '시험듦');
            if (absentees.length > 0 && notificationCount < MAX_NOTIFICATIONS) {
                const targetStudent = absentees[Math.floor(Math.random() * absentees.length)];
                const target = targetStudent.name;
                addNotification(
                    "빈자리가 느껴져요 �",
                    `${target}님이 요즘 통 안 보이네요. 부담 없는 '잘 지내?' 톡으로 먼저 손내밀어 주세요.`,
                    "error",
                    "pastoral",
                    targetStudent.id,
                    null,
                    "students"
                );
                notificationCount++;
            }

            // ⚠️ 분석 4: 연락처 누락 (행정, 우선순위 가장 낮음)
            const missingPhone = students.filter(s => !s.phone || s.phone.replace(/[^0-9]/g, '').length < 10);

            // 알림 슬롯이 남았을 때만 표시
            if (missingPhone.length > 0 && notificationCount < MAX_NOTIFICATIONS) {
                addNotification(
                    "데이터 점검 📝",
                    `연락처 없는 친구가 ${missingPhone.length}명 있어요. 지금 채워두면 급할 때 당황하지 않아요!`,
                    "warning"
                );
                notificationCount++;
            }

            // 체크 완료 표시
            localStorage.setItem(lastCheckKey, 'true');
            isChecked.current = true;
        };

        // 데이터 로딩 후 UX를 위해 3초 뒤에 분석 실행
        const timer = setTimeout(performAnalysis, 3000);

        return () => clearTimeout(timer);
    }, [students, userProfile, addNotification]);
};
